import { useCallback, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { Op, Hooks } from '@fortune-sheet/core';
import { hasContentOp } from '../useSheetEditorDrawerLogic';

export interface UseSheetSelectionStatusArgs {
    markWorkbookDirty: (ops: Op[]) => void;
}

export interface UseSheetSelectionStatusReturn {
    /** Bumped on every selection change AND on every content-carrying onOp
     *  batch — the StatusBar's own trigger to re-read getContext()/
     *  getSelection() (it never trusts the hook's own payload). */
    selectionTick: number;
    statusBarNotifyRef: RefObject<(() => void) | null>;
    hooks: Hooks;
    handleOp: (ops: Op[]) => void;
}

/**
 * Selection-change and onOp plumbing for the <Workbook>/StatusBar pair —
 * kept out of useSheetEditorDialogs, which otherwise has nothing to do with
 * selection tracking or dirty-marking.
 */
export const useSheetSelectionStatus = ({ markWorkbookDirty }: UseSheetSelectionStatusArgs): UseSheetSelectionStatusReturn => {
    const [selectionTick, setSelectionTick] = useState(0);
    const statusBarNotifyRef = useRef<(() => void) | null>(null);
    const afterSelectionChange = useCallback(() => {
        setSelectionTick((t) => t + 1);
        statusBarNotifyRef.current?.();
    }, []);
    // Referentially stable (SheetWorkbook's memo contract): both the
    // callback (empty deps) and this wrapper never change identity.
    const hooks = useMemo<Hooks>(() => ({ afterSelectionChange }), [afterSelectionChange]);

    const handleOp = useCallback(
        (ops: Op[]) => {
            markWorkbookDirty(ops);
            if (hasContentOp(ops)) statusBarNotifyRef.current?.();
        },
        [markWorkbookDirty],
    );

    return { selectionTick, statusBarNotifyRef, hooks, handleOp };
};
